'use client';
import { duration } from 'moment';
import React, { forwardRef, type FormEventHandler } from 'react';
import {
  FormProvider,
  useFormContext,
  type FieldValues,
  type UseFormReturn,
} from 'react-hook-form';
import { LoadingButton, type ButtonProps } from './loading-button';

export type UseZodForm<TInput extends FieldValues> = UseFormReturn<TInput> & {
  id: string;
  rateLimit: number;
  startQuery: FormEventHandler<HTMLFormElement>;
  doSubmit: (error?: () => void) => void;
};

export interface BodyMessageResult {
  status: number;
  body?: {
    message?: string;
  };
}

export type FormRootRefProps<TInput extends FieldValues> = Omit<
  React.ComponentProps<'form'>,
  'onSubmit' | 'id'
> & {
  prepareValues?: (value: TInput) => TInput;
  form: UseZodForm<TInput>;
};

interface WithForwardRefType extends React.FC<FormRootRefProps<FieldValues>> {
  <TInput extends FieldValues>(
    props: FormRootRefProps<TInput>
  ): ReturnType<React.FC<FormRootRefProps<TInput>>>;
}

export const FormRoot: WithForwardRefType = forwardRef(
  (props, ref?: React.Ref<HTMLFormElement>) => {
    const { form, ...passThrough } = props;
    return (
      <FormProvider {...form}>
        <form
          {...passThrough}
          ref={ref}
          id={form.id}
          onSubmit={form.startQuery}
        />
      </FormProvider>
    );
  }
);
FormRoot.displayName = 'FormRoot';

type SubmitButtonProps = Omit<ButtonProps, 'type' | 'form'> & {
  form?: UseZodForm<any>;
  loading?: boolean;
};

export function SubmitButton({
  loading,
  disabled,
  variant,
  ...props
}: SubmitButtonProps) {
  const context = useFormContext();

  const form = props.form ?? context;
  if (!form) {
    throw new Error(
      'SubmitButton must be used within a Form or have a form prop'
    );
  }

  const { formState, rateLimit } = form as UseFormReturn<
    FieldValues,
    any,
    undefined
  > & { rateLimit: number };

  return (
    <LoadingButton
      {...props}
      loading={formState.isSubmitting || rateLimit > 0 || loading}
      form={props.form?.id}
      variant={rateLimit > 0 ? 'destructive' : variant}
      disabled={formState.isSubmitting || rateLimit > 0 || disabled || loading}
    >
      {rateLimit > 0
        ? `Bitte warte ${duration(rateLimit).seconds()} Sekunden`
        : formState.isSubmitting
          ? 'Loading'
          : props.children}
    </LoadingButton>
  );
}

export function FormServerError({ ...props }) {
  const context = useFormContext();
  const form = props.form ?? context;
  if (!form) {
    throw new Error(
      'SubmitButton must be used within a Form or have a form prop'
    );
  }

  const { formState } = form;
  return (
    formState.errors.root?.server.message && (
      <p className='text-md rounded-sm border border-red-500 px-2 py-1 text-sm font-medium text-red-500'>
        {formState.errors.root?.server.message}
      </p>
    )
  );
}
